import produce from "immer";
import find from "lodash/find";
import findLast from "lodash/findLast";
import includes from "lodash/includes";
import map from "lodash/map";
import pullAllBy from "lodash/pullAllBy";
import { v1 as uuid } from "uuid";
import { CustomizationState } from "../";
import { Configuration } from "../../../types/customization/configuration";
import { ContentEditablePicture } from "../../../types/customization/content";
import { sizeP10kToMm } from "../../../utils/customization/box";
import { maxPageReached } from "../../../utils/customization/configuration";
import { calcNextCropping } from "../../../utils/customization/cropping";
import {
  findEditablePictureDefinition,
  findPage,
  findPageDefinition,
} from "../../../utils/customization/finders";
import {
  addPageMutation,
  generateNewPage,
  selectPageDefinitionsWithEditablePicture,
  sortElementsForRendering,
} from "../../../utils/customization/page";
import { getMaxPageReachedMessage } from "../../../utils/customMessages";
import { addNotificationRemovingSameOnes } from "../../../utils/notification";
import { setPageConfigMapping } from "./ui/setPageConfigMapping";

export interface AddPageAndSwapEditablePictureElementPictureAction {
  type: "addPageAndSwapEditablePictureElementPicture";
  elementFrom: {
    pageKey: string;
    editablePictureKey: string;
  };
  configuration: Configuration | undefined;
}

export function addPageAndSwapEditablePictureElementPicture(
  state: CustomizationState,
  action: AddPageAndSwapEditablePictureElementPictureAction
): CustomizationState {
  return produce(state, (draftState) => {
    const { content, template } = draftState;
    const { elementFrom, configuration } = action;

    if (maxPageReached(draftState.content, configuration)) {
      const maxPagesMessage = getMaxPageReachedMessage(
        draftState.content.templateType,
        configuration
      );
      const notification = {
        code: "KFE-003",
        customMessage: maxPagesMessage,
        key: uuid(),
      };
      addNotificationRemovingSameOnes(draftState.notifications, notification);
      return;
    }

    const pageFrom = findPage(elementFrom.pageKey, content);
    const pageDefinitionFrom = findPageDefinition(pageFrom.pageDefinitionKey, template);
    const editablePictureDefinitionFrom = findEditablePictureDefinition(
      elementFrom.editablePictureKey,
      pageDefinitionFrom
    );
    const elementFromSizeMm = sizeP10kToMm(editablePictureDefinitionFrom, pageDefinitionFrom);
    const ratioFrom = elementFromSizeMm.width.value / elementFromSizeMm.height.value;
    const editablePictureFrom = find(
      pageFrom.editablePictures,
      ({ editablePictureKey }) => editablePictureKey === elementFrom.editablePictureKey
    );

    if (!editablePictureFrom) {
      return;
    }

    const eligiblePageDefinitions = selectPageDefinitionsWithEditablePicture(template);
    const eligiblePageDefinitionKeys = map(eligiblePageDefinitions, "key");
    const lastPageWithEditablePictures = findLast(content.pages, (page) =>
      includes(eligiblePageDefinitionKeys, page.pageDefinitionKey)
    );
    const newPageDefinitionKey = lastPageWithEditablePictures
      ? lastPageWithEditablePictures.pageDefinitionKey
      : eligiblePageDefinitionKeys[0];
    const newPageDefinition = findPageDefinition(newPageDefinitionKey, template);
    const editablePictureDefinition = find(
      sortElementsForRendering(newPageDefinition.elementDefinitions),
      ({ type }) => type === "editable-picture"
    );

    if (!editablePictureDefinition || editablePictureDefinition.type !== "editable-picture") {
      return;
    }

    pullAllBy(pageFrom.editablePictures, [editablePictureFrom], "editablePictureKey");

    const newPage = generateNewPage(newPageDefinition.key, template);
    addPageMutation(newPage, draftState.content);

    const elementSizeMm = sizeP10kToMm(editablePictureDefinition, newPageDefinition);
    const ratioB = elementSizeMm.width.value / elementSizeMm.height.value;
    const newCropping = calcNextCropping({
      nextRatio: ratioB,
      prevCropping: editablePictureFrom.cropping,
      prevRatio: ratioFrom,
    });

    const newEditablePicture: ContentEditablePicture = {
      cropping: newCropping,
      editablePictureKey: editablePictureDefinition.key,
      filterTag: editablePictureFrom.filterTag,
      selectionPhotoKey: editablePictureFrom.selectionPhotoKey,
    };

    newPage.editablePictures.push(newEditablePicture);

    const result = setPageConfigMapping(draftState, {
      type: "setPageConfigMapping",
      configuration: draftState.template.contentPageConfiguration,
    });
    draftState.ui.pagesConfigMapping = result.ui.pagesConfigMapping;
  });
}
